import {
  CTCSS_TONES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 16
const MEMORY_SIZE = 0x4000
const CHANNEL_START = 0x2000
const CHANNEL_SIZE = 32
const CHANNEL_COUNT = 200
const NAME_LENGTH = 7
const SET_START = 0x0100
const SKIP_START = 0x0120
const TONES = [62.5, ...CTCSS_TONES]

type TH9000Spec = {
  id: string
  vendor: string
  model: string
  summary: string
}

export type TH9000Profile = {
  protocol: 'th9000'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 0
  channelMaximum: number
  nameLength: 7
  memorySize: number
  powerLevels: readonly string[]
}

const SPECS: readonly TH9000Spec[] = [
  {
    id: 'tyt-th9000-220',
    vendor: 'TYT',
    model: 'TH9000_220',
    summary: 'TYT TH-9000 220 MHz mobile.',
  },
  {
    id: 'tyt-th9000-144',
    vendor: 'TYT',
    model: 'TH9000_144',
    summary: 'TYT TH-9000 144 MHz mobile.',
  },
  {
    id: 'tyt-th9000-440',
    vendor: 'TYT',
    model: 'TH9000_440',
    summary: 'TYT TH-9000 440 MHz mobile.',
  },
  {
    id: 'luiton-lt-580-vhf',
    vendor: 'LUITON',
    model: 'LT-580_VHF',
    summary: 'LUITON LT-580 VHF mobile.',
  },
  {
    id: 'luiton-lt-580-uhf',
    vendor: 'LUITON',
    model: 'LT-580_UHF',
    summary: 'LUITON LT-580 UHF mobile.',
  },
  {
    id: 'retevis-rt9000d-136-174',
    vendor: 'Retevis',
    model: 'RT9000D_136-174',
    summary: 'Retevis RT9000D 136-174 MHz mobile.',
  },
  {
    id: 'retevis-rt9000d-220-260',
    vendor: 'Retevis',
    model: 'RT9000D_220-260',
    summary: 'Retevis RT9000D 220-260 MHz mobile.',
  },
  {
    id: 'retevis-rt9000d-400-490',
    vendor: 'Retevis',
    model: 'RT9000D_400-490',
    summary: 'Retevis RT9000D 400-490 MHz mobile.',
  },
  {
    id: 'retevis-rt9000d-66-88',
    vendor: 'Retevis',
    model: 'RT9000D_66-88',
    summary: 'Retevis RT9000D 66-88 MHz mobile.',
  },
]

export const TH9000_PROFILES: readonly TH9000Profile[] = SPECS.map((spec) => ({
  protocol: 'th9000',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '200 channels', '7-character names'],
  baudRate: 9600,
  channelMinimum: 0,
  channelMaximum: CHANNEL_COUNT - 1,
  nameLength: NAME_LENGTH,
  memorySize: MEMORY_SIZE,
  powerLevels: ['High', 'Mid', 'Low'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'High',
    skip: false,
  }
}

function decodeBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBcd(value: number, size: number): Uint8Array {
  const result = new Uint8Array(size)
  for (let index = size - 1; index >= 0; index -= 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

function channelOffset(number: number): number {
  return CHANNEL_START + number * CHANNEL_SIZE
}

function flagOffset(start: number, number: number): readonly [number, number] {
  return [start + Math.floor(number / 8), 1 << (7 - (number % 8))]
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(
  mode: number,
  ctcssIndex: number,
  dcsCode: number,
  extra: number,
  inverted: boolean,
): DecodedTone {
  if (mode === 1) {
    return {
      mode: 'ctcss',
      ctcss: TONES[ctcssIndex] ?? null,
      dtcs: null,
      polarity: 'N',
    }
  }
  if (mode === 2) {
    const index = dcsCode | (extra << 8)
    const code = Number(indexToDcs(index))
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number.isFinite(code) ? code : null,
      polarity: inverted ? 'R' : 'N',
    }
  }
  return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
}

function encodeTone(
  channel: WebChannel,
): {
  mode: number
  value: number
  extra: number
  inverted: number
} {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = TONES.indexOf(channel.ctcss)
    if (index < 0) throw new Error('That CTCSS tone is not supported by this radio.')
    return { mode: 1, value: index, extra: 0, inverted: 0 }
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = dcsToIndex(channel.dtcs)
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    return {
      mode: 2,
      value: index & 0xff,
      extra: index >> 8,
      inverted: channel.dtcsPolarity === 'R' ? 1 : 0,
    }
  }
  return { mode: 0, value: 0, extra: 0, inverted: 0 }
}

function indexToDcs(index: number): string {
  return index.toString(8).padStart(3, '0')
}

function dcsToIndex(code: number): number {
  const digits = code.toString()
  if (!/^[0-7]{3}$/.test(digits)) return -1
  return Number.parseInt(digits, 8)
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value < 0x20 || value >= 0x7f ? 0x20 : value)),
  ).trim()
}

export function parseTH9000Channel(
  image: Uint8Array,
  profile: TH9000Profile,
  number: number,
): WebChannel {
  const [setOffset, setBit] = flagOffset(SET_START, number)
  if (image[setOffset] & setBit) return emptyChannel(number)
  const offset = channelOffset(number)
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  let frequency = decodeBcd(raw.slice(0, 4)) * 100
  if (frequency % 1000 === 200 || frequency % 1000 === 700) frequency += 50
  if (!frequency) return emptyChannel(number)
  const duplex = (raw[10] >> 6) & 0x03
  const shift = decodeBcd(raw.slice(4, 8)) * 100
  const txFrequency =
    duplex === 1 || raw[9] & 0x80
      ? null
      : duplex === 2
        ? frequency - shift
        : duplex === 3
          ? frequency + shift
          : frequency
  const rxExtra = raw[9] & 0x01
  const txExtra = (raw[9] >> 1) & 0x01
  const rxInverted = (raw[9] & 0x04) !== 0
  const txInverted = (raw[9] & 0x08) !== 0
  const txTone = decodeTone(
    (raw[11] >> 6) & 0x03,
    (raw[12] >> 2) & 0x3f,
    raw[14],
    txExtra,
    txInverted,
  )
  const rxtone = decodeTone((raw[11] >> 4) & 0x03, (raw[13] >> 2) & 0x3f, raw[15], rxExtra, rxInverted)
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const [skipOffset, skipBit] = flagOffset(SKIP_START, number)
  const width = (raw[9] >> 4) & 0x03
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(19, 26)),
    rxFrequency: frequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: width === 2 ? 'NFM' : 'FM',
    power: profile.powerLevels[(raw[10] >> 4) & 0x03] ?? 'High',
    skip: (image[skipOffset] & skipBit) !== 0,
  }
}

export function parseTH9000Channels(
  image: Uint8Array,
  profile: TH9000Profile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, number) => parseTH9000Channel(image, profile, number),
  )
}

export function applyTH9000Channel(
  image: Uint8Array,
  profile: TH9000Profile,
  channel: WebChannel,
): void {
  if (channel.number < 0 || channel.number >= CHANNEL_COUNT) {
    throw new Error('Channel must be between 0 and 199.')
  }
  const [setOffset, setBit] = flagOffset(SET_START, channel.number)
  const [skipOffset, skipBit] = flagOffset(SKIP_START, channel.number)
  if (channel.empty) {
    image[setOffset] |= setBit
    image[skipOffset] |= skipBit
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  image[setOffset] &= ~setBit
  image[skipOffset] = channel.skip
    ? image[skipOffset] | skipBit
    : image[skipOffset] & ~skipBit
  const offset = channelOffset(channel.number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  const savedName = channel.name.slice(0, NAME_LENGTH).padEnd(NAME_LENGTH, ' ')
  const savedStep = raw[8]
  raw.fill(0)
  raw.set(encodeBcd(Math.round(channel.rxFrequency / 100), 4), 0)
  const tx = channel.txFrequency
  const delta = tx ? tx - channel.rxFrequency : 0
  raw.set(encodeBcd(Math.round(Math.abs(delta) / 100), 4), 4)
  raw[8] = savedStep & 0x0f
  const duplex = !tx ? 1 : delta < 0 ? 2 : delta > 0 ? 3 : 0
  raw[9] |= tx ? 0 : 0x80
  raw[10] =
    ((Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03) << 4) |
    ((duplex & 0x03) << 6)
  const txTone = encodeTone(channel)
  const rxTone = encodeTone(channel)
  raw[9] |=
    (rxTone.extra ? 0x01 : 0) |
    (txTone.extra ? 0x02 : 0) |
    (rxTone.inverted ? 0x04 : 0) |
    (txTone.inverted ? 0x08 : 0) |
    ((channel.mode === 'NFM' ? 2 : 1) << 4)
  raw[11] =
    ((rxTone.mode & 0x03) << 4) |
    ((txTone.mode & 0x03) << 6)
  raw[12] = (raw[12] & 0x03) | ((txTone.value & 0x3f) << 2)
  raw[13] = (raw[13] & 0x03) | ((rxTone.value & 0x3f) << 2)
  raw[14] = txTone.value & 0xff
  raw[15] = rxTone.value & 0xff
  for (let index = 0; index < NAME_LENGTH; index += 1) {
    raw[19 + index] = savedName.charCodeAt(index) & 0xff
  }
}

function checksum(data: Uint8Array): number {
  let total = 0
  for (const value of data) total = (total + value) & 0xff
  return total
}

async function echoedWrite(
  transport: RadioTransport,
  data: Uint8Array,
): Promise<void> {
  await transport.write(data)
  await transport.readExact(data.length, 2000)
}

async function enterProgrammingMode(
  transport: RadioTransport,
): Promise<void> {
  for (const command of ['PROGRA:', 'PROGRAM']) {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      await transport.discardInput()
      try {
        await echoedWrite(transport, new TextEncoder().encode(command))
        const response = await transport.readExact(3, 1200)
        if (response[0] === 0x51 && response[1] === 0x58 && response[2] === ACK) {
          await echoedWrite(transport, Uint8Array.of(0x02))
          const identity = await transport.readExact(16, 3000)
          const text = new TextDecoder().decode(identity)
          if (!text.includes('TH-9000')) {
            throw new Error('The connected radio is not a TH-9000/RT9000D model.')
          }
          return
        }
      } catch {
        // Try the next protocol spelling.
      }
    }
  }
  throw new Error('The radio did not enter TH-9000 programming mode.')
}

async function finishProgrammingMode(transport: RadioTransport): Promise<void> {
  await echoedWrite(transport, new TextEncoder().encode('END'))
  try {
    await transport.readExact(1, 250)
  } catch {
    // LUITON variants intentionally omit the final ACK.
  }
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const frame = Uint8Array.of(
    0x52,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
  await echoedWrite(transport, frame)
  const response = await transport.readExact(BLOCK_SIZE + 6, 4000)
  if (
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== BLOCK_SIZE ||
    response[BLOCK_SIZE + 5] !== ACK ||
    checksum(response.slice(1, BLOCK_SIZE + 4)) !== response[BLOCK_SIZE + 4]
  ) {
    throw new Error(`Invalid TH-9000 read reply at 0x${address.toString(16)}.`)
  }
  return response.slice(4, 4 + BLOCK_SIZE)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + BLOCK_SIZE + 2)
  frame.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  ])
  frame.set(data, 4)
  frame[BLOCK_SIZE + 4] = checksum(frame.slice(1, BLOCK_SIZE + 4))
  frame[BLOCK_SIZE + 5] = ACK
  await echoedWrite(transport, frame)
  if ((await transport.readExact(1, 3000))[0] !== ACK) {
    throw new Error(`The radio rejected write block 0x${address.toString(16)}.`)
  }
}

export async function readTH9000RadioWithTransport(
  profile: TH9000Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading TH-9000 memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await finishProgrammingMode(transport)
  }
}

export async function writeTH9000RadioWithTransport(
  profile: TH9000Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than TH-9000 memory.')
  }
  await enterProgrammingMode(transport)
  try {
    const first = 0x0100 / BLOCK_SIZE
    const total = MEMORY_SIZE / BLOCK_SIZE - first
    for (let index = 0; index < total; index += 1) {
      const address = 0x0100 + index * BLOCK_SIZE
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total,
        label: 'Writing TH-9000 memory',
      })
    }
  } finally {
    await finishProgrammingMode(transport)
  }
}
